import React, { useState, useRef, useEffect } from 'react'
import styles from './style.module.scss'
import { useTranslation } from 'react-i18next'
import loadingIcon from 'assets/images/gif/sync-ani.gif'
import attendeesIcon from 'assets/images/calendar/property-attendees.svg'
import inviteIcon from 'assets/images/calendar/invite-24.svg'
import { selectUser } from 'popups'
import classNames from 'classnames'
import * as calendarService from 'pages/calendar/modules/CalendarService'
// 순환 참조 방지를 위해 EventManager 대신 공유 유틸리티에서 import
import { isPersonaEmail, getDisplayEmailFromPersonaEmail } from 'utils/personaEmail'
import { isOrgSharedAccountEmail, getOuCodeFromEmail } from 'utils/orgSharedAccount'
import ouCalendarService from 'services/ouCalendar'
import useOrgSharedOwnerName from '../../hooks/useOrgSharedOwnerName'
import FolderUserListCell from '../FolderEditor/FolderUserListCell'
import { ACCEPTED, DECLINED, NO_RESPONSE, OWNER } from '../common/UserListCell'
import orgChartHelper from 'pages/contacts/components/OrgChart/Helper'
import settings from 'popups/Settings'
import { warningBase } from 'utils/ui'

interface Folder {
  url: string
  displayName: string
  isShared: boolean
  isReadOnly?: boolean
  owner: string
  calendarColor?: { _cdata: string }
}
interface Invitee {
  email: string
  name: string
  status: number
  isReadOnly: boolean
}
interface InviteeCandidate {
  name: string
  email: string
}
interface FolderShareInfoProps {
  folder: Folder
  onClose: () => void
}
// 공유 불가 안내 팝업의 예상 높이(px). 제목 + 한 줄 안내 기준으로, 뒤에 있는 공유 모달의
// 세로 중앙에 팝업 중심을 맞추는 top 계산에 사용한다.
const CANNOT_INVITE_POPUP_HEIGHT = 200

const FolderShareInfo: React.FC<FolderShareInfoProps> = ({ folder, onClose }) => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [invitees, setInvitees] = useState<Invitee[]>([])
  const firstLoad = useRef<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const ownerOuName = useOrgSharedOwnerName(folder?.owner)

  // 좌우는 antd 기본 가운데 정렬을 유지하고, top 만 뒤에 있는 공유 모달의 세로 중앙에 맞춘다.
  // 모달 rect 를 구하지 못하면 화면 세로 중앙(centered) 으로 fallback.
  const showCannotInvitePopup = (names: string) => {
    const modalRect = containerRef.current?.closest('.ant-modal')?.getBoundingClientRect()
    warningBase({
      title: t('calendar.share.cannotInviteTitle'),
      content: t('calendar.share.cannotInvite', { names }),
      ...(modalRect
        ? {
            style: {
              top: modalRect.top + modalRect.height / 2 - CANNOT_INVITE_POPUP_HEIGHT / 2,
            },
          }
        : { centered: true }),
    })
  }

  const loadInvitees = async (): Promise<void> => {
    setIsLoading(true)
    calendarService.getCalendarInfo(folder.url, async (result: any) => {
      console.log('get calendar info result %o', result)

      const user = result?.props?.invite?.user
      if (!user) {
        setInvitees([])
        setIsLoading(false)
        return
      }
      let theInvitees: Invitee[] = []
      let users = user
      if (!Array.isArray(user)) {
        users = [user]
      }

      for (let i = 0; i < users.length; i++) {
        const user = users[i]
        let email = user.href.toLowerCase().startsWith('mailto:') ? user.href.slice(7) : user.href
        const status = user.inviteAccepted ? ACCEPTED : user.inviteDeclined ? DECLINED : NO_RESPONSE

        if (isPersonaEmail(email)) {
          email = getDisplayEmailFromPersonaEmail(email)
        }

        const contact = await orgChartHelper.findContact(email)
        let name: string
        if (isOrgSharedAccountEmail(email)) {
          await ouCalendarService.load()
          const ouCode = getOuCodeFromEmail(email)
          name =
            (ouCode && ouCalendarService.getOuName(ouCode)) || t('calendar.share.orgSharedAccount')
        } else {
          name = contact?.name || email.split('@')[0]
        }

        const userInfo = {
          email: email,
          name: name,
          status: status,
          isReadOnly: user.access.read,
        }
        theInvitees.push(userInfo)
      }

      const locale = settings.getLocale()
      theInvitees = theInvitees.sort((a, b) => a.name.localeCompare(b.name, locale))

      setInvitees(theInvitees)
      setIsLoading(false)
    })
  }

  useEffect(() => {
    if (!firstLoad.current) {
      firstLoad.current = true
      if (folder && !folder.isShared) {
        loadInvitees()
      }
    }
  }, [folder])

  const openSelectUser = (options: any) => {
    const { title } = options

    const addedList = invitees.map((invitee) => invitee.email)
    const userInfo = calendarService.getUserInfo()
    const prevEmailList = [...addedList, userInfo.email]
    const prevEmailSet = new Set(prevEmailList.map((email) => email.toLowerCase()))

    selectUser({
      userOnly: true,
      title: title || '',
      defaultEmailList: prevEmailList,
      excludeEmailList: [],
      disableEmails: prevEmailList,
      appendUserTitles: { [userInfo.email]: t('settings.calendarTabs.owner') } as any,
      onOk: async (data: any, closeFunc: any) => {
        console.log(' -- selected user : ', data)
        const { selectedUserList } = data

        // 소유자와 이미 초대된 사용자는 defaultEmailList 로 미리 선택된 상태로 되돌아오므로 제외한다.
        // (기존 초대자 재전송은 사용자 목록 셀의 '다시 공유하기' 메뉴가 담당)
        const userInfoList: InviteeCandidate[] = selectedUserList
          .filter((item) => !prevEmailSet.has(item.email.toLowerCase()))
          .map((item) => {
            const { name, email } = item
            return { name, email }
          })

        if (userInfoList.length === 0) {
          closeFunc()
          return
        }

        setIsLoading(true)
        const checkResults = await calendarService.checkInvitees(
          userInfoList.map((candidate) => candidate.email),
        )
        const blockedEmails = new Set(
          checkResults.filter((result) => !result.invitable).map((result) => result.email),
        )
        const invitableList = userInfoList.filter(
          (candidate) => !blockedEmails.has(candidate.email),
        )
        const blockedList = userInfoList.filter((candidate) => blockedEmails.has(candidate.email))

        closeFunc()

        if (blockedList.length > 0) {
          showCannotInvitePopup(
            blockedList.map((candidate) => candidate.name || candidate.email).join(', '),
          )
        }

        if (invitableList.length === 0) {
          setIsLoading(false)
          return
        }

        let requestCount = invitableList.length
        invitableList.forEach((invitee) => {
          calendarService.sendInviteCalendar(
            folder.url,
            folder.displayName,
            invitee.email,
            false,
            (_success) => {
              requestCount--
              if (requestCount === 0) {
                loadInvitees()
              } else {
                setIsLoading(false)
              }
            },
          )
        })
      },
    })
  }

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, { [styles.shared]: folder?.isShared })}
    >
      <div className={styles.header}>
        <h2>{folder?.displayName || t('calendar.share.title')}</h2>
      </div>
      {folder && folder?.isShared && (
        <React.Fragment>
          <div className={styles.sharedLabelWith}>{t('calendar.share.sharedWithYou')}</div>
          <div className={styles.permission}>
            {t('settings.calendarTabs.permission')} :{' '}
            {folder.isReadOnly
              ? t('settings.calendarTabs.viewOnly')
              : t('settings.calendarTabs.viewAndEdit')}
          </div>

          <div className={styles.sharedInfoUserCell}>
            <FolderUserListCell
              key={folder.owner}
              showMore={false}
              onChangePermission={() => {}}
              onStopSharing={() => {}}
              email={folder.owner}
              name={
                isOrgSharedAccountEmail(folder.owner)
                  ? (ownerOuName ?? t('calendar.share.orgSharedAccount'))
                  : undefined
              }
              nameSuffix={
                <div style={{ marginLeft: '4px', color: folder.calendarColor._cdata }}>
                  {t('settings.calendarTabs.owner')}
                </div>
              }
              status={OWNER}
            />
          </div>
          <div style={{ height: '16px' }}></div>
        </React.Fragment>
      )}
      {folder && !folder.isShared && (
        <>
          <div className={styles.sharedLabel}>
            {t('calendar.edit.sharedByYou')}
            <button className={styles.inviteButton}>
              <img
                src={inviteIcon}
                alt="Invite"
                onClick={() =>
                  openSelectUser({ title: t('calendar.edit.invitation'), key: 'invitation' })
                }
              />
            </button>
          </div>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <img src={loadingIcon} alt="Loading" />
            </div>
          ) : invitees.length > 0 ? (
            <div className={styles.invitees}>
              {invitees.map((invitee) => (
                <FolderUserListCell
                  key={invitee.email}
                  name={invitee.name}
                  email={invitee.email}
                  status={invitee.status}
                  isReadOnly={invitee.isReadOnly}
                  showMore={true}
                  onChangePermission={(isReadOnly) => {
                    // 권한 변경 처리
                    console.log('onChangePermission %o', isReadOnly)
                    setIsLoading(true)
                    calendarService.sendInviteCalendar(
                      folder.url,
                      folder.displayName,
                      invitee.email,
                      isReadOnly,
                      (success) => {
                        if (success) {
                          loadInvitees()
                        } else {
                          setIsLoading(false)
                        }
                      },
                    )
                  }}
                  onStopSharing={() => {
                    // 공유 중지 처리
                    console.log('onStopSharing')
                    setIsLoading(true)
                    calendarService.deleteInviteCalendar(folder.url, invitee.email, (success) => {
                      if (success) {
                        loadInvitees()
                      } else {
                        setIsLoading(false)
                      }
                    })
                  }}
                />
              ))}
            </div>
          ) : (
            <div className={styles.noInvitees}>
              <img src={attendeesIcon} alt="No users" />
              {t('calendar.edit.NoSharedUser')}
            </div>
          )}
        </>
      )}
      <div className={styles.footer}>
        <button className={styles.closeButton} onClick={onClose}>
          {t('common.button.close')}
        </button>
      </div>
    </div>
  )
}

export default FolderShareInfo
